import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  IconButton,
  Box,
  Button,
  Link,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";

import logo from "../../../assets/images/logo.png";

// Constantes de diseño
const PRIMARY_BLUE = "#0A56A2";
const SECONDARY_BLUE = "#4669E8";
const HOVER_BLUE = "#2952CC";
const HEADER_HEIGHT_MOBILE = "60px";
const HEADER_HEIGHT_DESKTOP = "70px";
const HEADER_BORDER_RADIUS = "16px";
const EXPAND_DURATION = "350ms";

const LoginButton = styled(Button)(() => ({
  backgroundColor: SECONDARY_BLUE,
  color: "white",
  borderRadius: "20px",
  textTransform: "none",
  fontFamily: "Quicksand-Regular",
  fontSize: "15px",
  fontWeight: "normal",
  padding: "8px 24px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: SECONDARY_BLUE, // Agregado: mantener color original en hover
    boxShadow: "none", // Agregado: sin sombra en hover
  },
  "&:focus": {
    backgroundColor: SECONDARY_BLUE, // Agregado: mantener color original en focus
    boxShadow: "none", // Agregado: sin sombra en focus
  },
  "&:focus-visible": {
    backgroundColor: SECONDARY_BLUE, // Agregado: mantener color original en focus visible
    boxShadow: "none", // Agregado: sin sombra en focus visible
    outline: "none", // Agregado: sin outline en focus
  },
  "&:active": {
    backgroundColor: HOVER_BLUE,
    boxShadow: "none",
  },
}));

const NavButton = styled(Typography)(() => ({
  fontFamily: "Quicksand-Regular",
  fontSize: "15px",
  cursor: "pointer",
  textDecoration: "none",
  padding: "8px 16px",
  borderRadius: "30px",
  transition: "all 0.3s ease",
  "&:hover": {
    color: "white",
    backgroundColor: SECONDARY_BLUE,
  },
}));

const MobileMenuOption = styled(Typography)(() => ({
  fontFamily: "Quicksand-Regular",
  fontSize: "16px", // Reducido de 20px para consistencia con tipografía del landing
  cursor: "pointer",
  textDecoration: "none",
  color: "#333",
  marginBottom: "24px",
  transition: "all 0.3s ease",
  textAlign: "center",
  "&:hover": {
    color: SECONDARY_BLUE,
  },
}));

const RegisterLink = styled(Typography)(() => ({
  fontFamily: "Quicksand-Regular",
  fontSize: "16px",
  cursor: "pointer",
  color: SECONDARY_BLUE,
  textAlign: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    color: HOVER_BLUE,
    textDecoration: "underline",
  },
}));

export default function HeaderProfesionales({ scrollToPricing }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Resetear menú móvil cuando cambia el breakpoint
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  // Cerrar menú móvil al hacer scroll
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      if (mobileMenuOpen && window.scrollY > 50) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileMenuOpen, isMobile]);

  // Prevenir scroll del body cuando el menú está abierto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const scrollToSection = useCallback((sectionId) => {
    if (window.location.pathname !== "/pro") {
      window.location.href = `/pro#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleNavClick = useCallback(
    (sectionId) => {
      if (isMobile) {
        setMobileMenuOpen(false);
        setTimeout(() => scrollToSection(sectionId), 400);
      } else {
        scrollToSection(sectionId);
      }
    },
    [isMobile, scrollToSection]
  );

  const handlePricingClick = useCallback(() => {
    const handleNavigation = () => {
      if (window.location.pathname !== "/pro") {
        window.location.href = "/pro#planes-section";
      } else if (scrollToPricing) {
        scrollToPricing();
      }
    };

    if (isMobile) {
      setMobileMenuOpen(false);
      setTimeout(handleNavigation, 400);
    } else {
      handleNavigation();
    }
  }, [isMobile, scrollToPricing]);

  const toggleMobileMenu = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setMobileMenuOpen((prev) => !prev);
    setTimeout(() => setIsAnimating(false), 350);
  }, [isAnimating]);

  // NUEVO: Función helper para tracking unificado en Header
  const trackAction = () => {
    if (window.fbq) {
      window.fbq('trackCustom', 'inicioregistro');
    }
  };

  // Desktop Header
  if (!isMobile) {
    return (
      <Box
        sx={{
          position: "fixed", // Cambiado de sticky a fixed
          top: "24px",
          left: "50%", // Centrado horizontal
          transform: "translateX(-50%)", // Centrado horizontal
          maxWidth: "1200px", // Equivalente a Container lg
          width: "calc(100% - 48px)", // Simula padding lateral del Container (24px cada lado)
          zIndex: 1100,
          background: "rgba(255, 255, 255, 0.4)", // Cambiado: más transparente
          backdropFilter: "blur(20px)", // Agregado: efecto blur
          WebkitBackdropFilter: "blur(20px)", // Agregado: soporte Safari
          borderRadius: HEADER_BORDER_RADIUS,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          // Removido: transition y &:hover para eliminar efecto de sombra
        }}
      >
                <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: HEADER_HEIGHT_DESKTOP,
            px: 3,
            position: "relative", // Añadido para posicionamiento absoluto de navegación
          }}
        >
          {/* Logo */}
          <Box sx={{ flexShrink: 0 }}>
            <Link href="/pro" underline="none">
              <img
                src={logo}
                alt="Logo clinifica"
                style={{ height: "40px", display: "block" }}
              />
            </Link>
          </Box>

          {/* Navegación Central */}
          <Box
            sx={{
              position: "absolute", // Cambiado a posicionamiento absoluto
              left: "50%", // Centrado horizontal
              transform: "translateX(-50%)", // Centrado horizontal exacto
              display: "flex",
              gap: 4,
            }}
          >
            <NavButton onClick={() => handleNavClick("caracteristicas")}>
              Características
            </NavButton>
            <NavButton onClick={handlePricingClick}>Precio</NavButton>
            <NavButton onClick={() => handleNavClick("contacto")}>
              Contacto
            </NavButton>
          </Box>

          {/* Botón Login */}
          <Box sx={{ flexShrink: 0 }}>
            <Link
              href="https://pro.clinifica.com"
              underline="none"
              style={{ textDecoration: "none" }}
              onClick={trackAction} // NUEVO: Tracking Desktop Login
            >
              <LoginButton variant="contained" disableRipple disableElevation>Iniciar sesión</LoginButton>
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  // Mobile Header
  return (
    <>
      {/* Backdrop */}
      {mobileMenuOpen && (
        <Box
          onClick={() => setMobileMenuOpen(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1099,
            backgroundColor: "transparent",
          }}
        />
      )}

      {/* Header Móvil */}
      <Box
        sx={{
          position: "fixed", // Cambiado de sticky a fixed
          top: "16px",
          left: "50%", // Centrado horizontal
          transform: "translateX(-50%)", // Centrado horizontal
          maxWidth: "1152px", // Ancho efectivo del contenido en Container lg (1200px - 48px padding)
          width: "calc(100% - 32px)", // Simula padding lateral móvil (16px cada lado)
          zIndex: 1100,
          background: "rgba(255, 255, 255, 0.4)", // Cambiado: más transparente
          backdropFilter: "blur(20px)", // Agregado: efecto blur
          WebkitBackdropFilter: "blur(20px)", // Agregado: soporte Safari
          borderRadius: HEADER_BORDER_RADIUS,
          boxShadow: mobileMenuOpen
            ? "0 8px 32px rgba(0, 0, 0, 0.15)"
            : "0 2px 8px rgba(0, 0, 0, 0.08)",
          transition: `all ${EXPAND_DURATION} ease-out`,
          maxHeight: mobileMenuOpen ? "500px" : HEADER_HEIGHT_MOBILE,
          overflow: "hidden",
        }}
      >
        {/* Header cerrado */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: HEADER_HEIGHT_MOBILE,
            px: 2,
          }}
        >
          <Link href="/pro" underline="none">
            <img
              src={logo}
              alt="Logo clinifica"
              style={{ height: "30px", display: "block" }}
            />
          </Link>

          <IconButton
            onClick={toggleMobileMenu}
            disabled={isAnimating}
            sx={{ color: "#aeaeae" }}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        {/* Menú expandido */}
        {mobileMenuOpen && (
          <Box
            sx={{
              px: 3,
              pb: 4,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              opacity: mobileMenuOpen ? 1 : 0,
              transition: `opacity 300ms ease-out ${mobileMenuOpen ? "150ms" : "0ms"}`,
            }}
          >
            {/* Opciones de navegación */}
            <Box sx={{ mb: 4, mt: 2 }}>
              <MobileMenuOption onClick={() => handleNavClick("caracteristicas")}>
                Características
              </MobileMenuOption>
              <MobileMenuOption onClick={handlePricingClick}>
                Precio
              </MobileMenuOption>
              <MobileMenuOption onClick={() => handleNavClick("contacto")}>
                Contacto
              </MobileMenuOption>
            </Box>

            {/* Botón Iniciar sesión */}
            <Box sx={{ width: "100%", mb: 2 }}>
              <Link
                href="https://pro.clinifica.com"
                underline="none"
                style={{ textDecoration: "none" }}
                onClick={trackAction} // NUEVO: Tracking Mobile Login
              >
                <LoginButton
                  variant="contained"
                  disableRipple
                  disableElevation
                  sx={{ width: "100%", py: 0.5 }}
                >
                  Iniciar sesión
                </LoginButton>
              </Link>
            </Box>

            {/* Link Registrarte */}
            <Link
              href="/pro/registro"
              underline="none"
              style={{ textDecoration: "none" }}
              onClick={trackAction} // NUEVO: Tracking Mobile Registro
            >
              <RegisterLink>Registrarte</RegisterLink>
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
}
